import {
  SparkStructureDto,
  SparkTreeLevel,
  SparkTreeNodeDto,
} from '@/app/common/general-types/spark-ads-v3.dto';

/** A tree node flattened for rendering, carrying its depth and open state. */
export interface FlatTreeRow {
  node: SparkTreeNodeDto;
  depth: number;
  hasChildren: boolean;
  isOpen: boolean;
}

/**
 * Walks the tree, emitting only rows whose ancestors are open.
 *
 * Flattening rather than rendering recursively keeps every row a direct child of the
 * same grid, which is what makes the columns line up across all three depths.
 */
export function flattenTree(
  nodes: readonly SparkTreeNodeDto[],
  isOpen: (nodeId: string) => boolean,
  depth = 0,
): FlatTreeRow[] {
  const rows: FlatTreeRow[] = [];

  for (const node of nodes) {
    const hasChildren = node.children.length > 0;
    const open = hasChildren && isOpen(node.id);
    rows.push({ node, depth, hasChildren, isOpen: open });

    if (open) {
      rows.push(...flattenTree(node.children, isOpen, depth + 1));
    }
  }

  return rows;
}

/** Every node id that has children — what "Expand all" needs to open. */
export function expandableNodeIds(nodes: readonly SparkTreeNodeDto[]): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    if (node.children.length > 0) {
      ids.push(node.id);
      ids.push(...expandableNodeIds(node.children));
    }
  }
  return ids;
}

const LEVEL_TAGS: Record<SparkTreeLevel, string> = {
  campaign: 'CMP',
  adgroup: 'ADG',
  ad: 'AD',
};

export function levelTag(level: SparkTreeLevel): string {
  return LEVEL_TAGS[level];
}

const LEVEL_TAG_CLASSES: Record<SparkTreeLevel, string> = {
  campaign: 'bg-brand-studio text-white',
  adgroup: 'bg-brand-studio-dark text-white',
  ad: 'bg-brand-deep-purple text-white',
};

export function levelTagClass(level: SparkTreeLevel): string {
  return LEVEL_TAG_CLASSES[level];
}

const LEVEL_NAMES: Record<SparkTreeLevel, string> = {
  campaign: 'Campaign',
  adgroup: 'Ad group',
  ad: 'Spark ad',
};

export function levelName(level: SparkTreeLevel): string {
  return LEVEL_NAMES[level];
}

/**
 * Turns TikTok's status vocabulary into a label and pill class.
 *
 * TikTok reports both an operation status (`ENABLE`/`DISABLE`) and a much more
 * granular secondary status; the secondary one is what distinguishes "in review" from
 * "delivering", so it wins where present.
 */
export function statusFor(node: SparkTreeNodeDto): { label: string; pillClass: string } {
  const secondary = (node.secondaryStatus ?? '').toUpperCase();
  const operation = (node.status ?? '').toUpperCase();

  if (secondary.includes('AUDIT') || secondary.includes('REVIEW')) {
    return { label: 'In review', pillClass: 'pill pill-amber' };
  }
  if (secondary.includes('REJECT') || secondary.includes('DISAPPROVE')) {
    return { label: 'Rejected', pillClass: 'pill pill-red' };
  }
  if (operation === 'DISABLE' || secondary.includes('DISABLE') || secondary.includes('PAUSE')) {
    return { label: 'Paused', pillClass: 'pill pill-gray' };
  }
  if (secondary.includes('DELETE')) {
    return { label: 'Deleted', pillClass: 'pill pill-gray' };
  }
  if (operation === 'ENABLE') {
    return { label: 'Active', pillClass: 'pill pill-green' };
  }
  return { label: 'Unknown', pillClass: 'pill pill-gray' };
}

export function isTreeEmpty(structure: SparkStructureDto | undefined): boolean {
  return !structure || structure.nodes.length === 0;
}
